import { toDateKey, toIso } from '../lib/format';
import type { Client, EntityMap, Job, JobRecord, LatLng, Namespace, PestActivity, PhotoRef, RodentPlan, Station, StationType, Technician } from './types';

type Raw = Record<string, any>;

const str = (value: unknown, fallback = '') => (typeof value === 'string' ? value : value === undefined || value === null ? fallback : String(value));
const latLng = (value: unknown): LatLng | null => (Array.isArray(value) && value.length === 2 && value.every((item) => Number.isFinite(Number(item))) ? [Number(value[0]), Number(value[1])] : null);
const STATION_TYPES: StationType[] = ['non-chemical', 'fgar', 'sgar', 'monitoring', 'tracking-powder'];
const PEST_CODES: Record<string, string> = { C: 'Cockroaches', A: 'Ants', S: 'Spiders', R: 'Rodents', V: 'Termite inspection' };

function client(raw: Raw, id: string): Client {
  return { id, name: str(raw.name, 'Unnamed client'), type: str(raw.type, 'Residential'), highRisk: Boolean(raw.highRisk), contact: str(raw.contact), phone: str(raw.phone), email: str(raw.email), address: str(raw.address), notes: str(raw.notes), location: latLng(raw.location) };
}

function job(raw: Raw, id: string): Job {
  // Prototype jobs only had `service` (code) and `detail` (comma separated labels).
  const detailNames = str(raw.detail).split(',').map((item) => item.trim()).filter(Boolean);
  const services: string[] = Array.isArray(raw.services) ? raw.services.map(String) : detailNames.filter((name) => Object.values(PEST_CODES).includes(name));
  const additional = raw.additional !== undefined ? str(raw.additional) : detailNames.find((name) => !Object.values(PEST_CODES).includes(name)) || '';
  return {
    id,
    clientId: raw.clientId || undefined,
    clientName: str(raw.clientName ?? raw.client, 'Client not recorded'),
    services,
    additional,
    serviceCode: str(raw.serviceCode ?? raw.service),
    detail: str(raw.detail),
    date: str(raw.date),
    startTime: str(raw.startTime, '09:00'),
    duration: Number(raw.duration) || 60,
    techId: raw.techId || undefined,
    techName: str(raw.techName ?? raw.tech),
    quoteAmount: Number(raw.quoteAmount) || 0,
    preAdvice: str(raw.preAdvice),
    notes: str(raw.notes),
    cancelled: Boolean(raw.cancelled),
    completed: Boolean(raw.completed),
    completedAt: raw.completedAt ? toIso(raw.completedAt) : undefined,
    completedBy: raw.completedBy || undefined,
    createdAt: toIso(raw.createdAt)
  };
}

function technician(raw: Raw, id: string): Technician {
  const contract = raw.contract && typeof raw.contract === 'object' && raw.contract.fileId ? { fileId: str(raw.contract.fileId), name: str(raw.contract.name) } : null;
  return {
    id,
    name: str(raw.name, 'Unnamed technician'),
    workType: str(raw.workType, 'Employee'),
    phone: str(raw.phone),
    email: str(raw.email),
    contract,
    contractName: contract ? contract.name : str(raw.contractName ?? (typeof raw.contract === 'string' ? raw.contract : '')),
    emergencyName: str(raw.emergencyName),
    emergencyPhone: str(raw.emergencyPhone),
    licence: str(raw.licence),
    expiry: str(raw.expiry),
    competencies: Array.isArray(raw.competencies) ? raw.competencies.map(String) : []
  };
}

function photos(raw: Raw): PhotoRef[] {
  const list = Array.isArray(raw.photos) ? raw.photos : Array.isArray(raw.data?.photos) ? raw.data.photos : [];
  return list.map((photo: Raw) => ({ fileId: photo.fileId || undefined, name: str(photo.name, 'Job evidence photo'), type: str(photo.type, 'image/jpeg'), capturedAt: str(photo.capturedAt), dataUrl: photo.dataUrl || undefined }));
}

function jobRecord(raw: Raw, id: string): JobRecord {
  const data: Record<string, unknown> = { ...(raw.data || {}) };
  delete data.photos;
  // Prototype ARMP reviews lived under metadata.armpReview.
  const review = raw.review ?? raw.metadata?.armpReview ?? null;
  const jobRef = str(raw.jobRef);
  const [, refDate, refTime] = jobRef.split('|');
  return {
    id,
    jobId: raw.jobId && typeof raw.jobId === 'string' ? raw.jobId : undefined,
    clientId: raw.clientId || undefined,
    clientName: str(raw.clientName ?? raw.client, 'Client not recorded'),
    jobService: str(raw.jobService, 'Service record'),
    technician: str(raw.technician),
    jobDate: str(raw.jobDate ?? (/^\d{4}-\d{2}-\d{2}$/.test(refDate || '') ? refDate : '')),
    jobTime: str(raw.jobTime ?? refTime ?? ''),
    type: (raw.type || 'Post-treatment advice') as JobRecord['type'],
    createdAt: toIso(raw.createdAt ?? (typeof raw.id === 'number' ? raw.id : undefined)),
    createdBy: raw.createdBy || undefined,
    data,
    photos: photos(raw),
    review
  };
}

function pestActivity(raw: Raw, id: string): PestActivity {
  return { id, clientId: raw.clientId || undefined, clientName: str(raw.clientName ?? raw.client), pest: str(raw.pest), status: raw.status === 'Inactive' ? 'Inactive' : 'Active', notes: str(raw.notes), date: toIso(raw.date), location: latLng(raw.location), jobRecordId: raw.jobRecordId || undefined };
}

function station(raw: Raw): Station | null {
  const lat = Number(raw.lat); const lng = Number(raw.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng, type: STATION_TYPES.includes(raw.type) ? raw.type : 'fgar' };
}

function rodentPlan(raw: Raw, id: string): RodentPlan {
  // Prototype plans: snapshotStations/snapshotOrigin/snapshotCrop, or only [lat, lng] station pairs.
  const source: unknown[] = Array.isArray(raw.snapshotStations) ? raw.snapshotStations : Array.isArray(raw.stations) ? raw.stations : [];
  const stations = source.map((item) => (Array.isArray(item) ? station({ lat: item[0], lng: item[1], type: 'fgar' }) : station(item as Raw))).filter(Boolean) as Station[];
  const snapshot = raw.snapshot ?? (raw.snapshotOrigin ? { originX: Number(raw.snapshotOrigin.originX), originY: Number(raw.snapshotOrigin.originY), crop: raw.snapshotCrop || { scale: 1, x: 0, y: 0 } } : null);
  return {
    id,
    clientId: raw.clientId || undefined,
    clientName: str(raw.clientName ?? raw.client),
    address: str(raw.address),
    location: latLng(raw.location) || [-19.2589, 146.8169],
    stations,
    snapshot,
    snapshotFileId: raw.snapshotShared ? null : raw.snapshotFileId || null,
    snapshotDocumentId: raw.snapshotDocumentId ?? (raw.snapshotShared ? raw.snapshotFileId : null) ?? null,
    snapshotFileName: str(raw.snapshotFileName),
    savedAt: toIso(raw.savedAt),
    lastSiteReviewDate: raw.lastSiteReviewDate ? toDateKey(raw.lastSiteReviewDate) : undefined,
    lastSiteReviewedBy: raw.lastSiteReviewedBy || undefined
  };
}

const normalizers: { [N in Namespace]: (raw: Raw, id: string) => EntityMap[N] } = {
  clients: client,
  jobs: job,
  technicians: technician,
  'job-records': jobRecord,
  'pest-activity': pestActivity,
  'rodent-plans': rodentPlan
};

export function normalize<N extends Namespace>(namespace: N, raw: unknown, id: string): EntityMap[N] {
  return normalizers[namespace]((raw && typeof raw === 'object' ? raw : {}) as Raw, id);
}

/** Removes device-only sync fields before a value is sent to the server. */
export function serverValue<T extends object>(value: T): T {
  const copy = { ...value } as Record<string, unknown>;
  delete copy._rev;
  delete copy._updatedAt;
  return copy as T;
}
